import FormField from 'components/FormField';
import { AiModelState, getPrefixName, Inline, Prefix, stores2storeScopes } from './share';
import { InputNumber, Radio, Select } from 'antd';
import { useCallback, useMemo } from 'react';
import { updateShelfLife } from 'views/BootGuide/service';
import { getStoreCount } from 'components/StoreScopes';
import useSmartField from 'hooks/useSmartField';
import { TimeContainer } from './Warranty';
import ModalMode from './ModalMode';
import { useTranslate } from '@tolgee/react';

type FV = BootGuide.AiModel.FormValues.ShelfLife;

function useLabel() {
  const { t } = useTranslate();

  return useMemo(() => {
    return {
      noSupport: t('参考详情页'),
      support: t('有保质期/有效期'),
    } as const;
  }, [t]);
}

function ShelfLife({ prefix }: { prefix?: Prefix }) {
  const label = useLabel();

  const {
    field: { value, onChange },
  } = useSmartField<FV['value']>(getPrefixName('value', prefix));
  const {
    field: { value: unit, onChange: onUnitChange },
  } = useSmartField<FV['unit']>(getPrefixName('unit', prefix));

  return (
    <div className="mt-[6px]">
      {Object.keys(label).map((key) => {
        return (
          <div key={key} className="mb-2">
            <Radio
              checked={value === key}
              onChange={({ target: { checked } }) => {
                checked && onChange(key);
                if (key === 'support' && !unit) {
                  onUnitChange('YEAR');
                }
              }}
            >
              {label[key]}
            </Radio>
          </div>
        );
      })}
      <TimeContainer background="#F7F9FA">
        <Time prefix={prefix} />
      </TimeContainer>
    </div>
  );
}

function Time({ prefix }: { prefix?: Prefix }) {
  const { t } = useTranslate();

  const {
    field: { value },
  } = useSmartField<FV['value']>(getPrefixName('value', prefix));
  const {
    field: { value: storeScopes },
  } = useSmartField<FV['storeScopes']>(getPrefixName('storeScopes', prefix));
  const {
    field: { value: unit, onChange: onUnitChange },
  } = useSmartField<FV['unit']>(getPrefixName('unit', prefix));

  if (value !== 'support' || !getStoreCount(storeScopes)) {
    return <></>;
  }

  return (
    <FormField<FV['number']>
      name={getPrefixName('number', prefix)}
      required
      label={t('保质期/有效期')}
      validate={(value) => (value ? undefined : t('department.请输入x', { x: t('保质期/有效期') }))}
    >
      {({ field }) => {
        return (
          <Inline>
            <InputNumber min={1} max={99} precision={0} {...(field as any)} />
            <Select
              className="!w-auto"
              dropdownMatchSelectWidth={false}
              value={unit}
              onChange={onUnitChange}
              options={[
                { value: 'YEAR', label: t('年') },
                { value: 'MONTH', label: t('月') },
              ]}
            />
          </Inline>
        );
      }}
    </FormField>
  );
}

export function ShelfLifeBootGuide(props: AiModelState<FV>) {
  const label = useLabel();

  return (
    <ModalMode
      state={props}
      title={props.t('保质期/有效期')}
      tips={props.t('您的店铺中的商品保质期/有效期是?')}
      form={<ShelfLife />}
      getFirstInitialValues={useCallback(({ stores }): { values: FV[] } => {
        return {
          values: [
            {
              value: 'noSupport',
              storeScopes: stores2storeScopes(stores),
              number: 1,
              unit: 'YEAR',
            },
            { value: 'support', storeScopes: [], number: 1, unit: 'YEAR' },
          ],
        };
      }, [])}
      updateApi={updateShelfLife}
      getFirstFormItem={({ value }, index, storeSelect) => {
        return (
          <div>
            <section>
              {label[value]}
              {storeSelect}
            </section>
            <TimeContainer background="#fff">
              <Time prefix={['values', index]} />
            </TimeContainer>
          </div>
        );
      }}
      getFirstFormStoreSelectKey={({ value }) => value}
    />
  );
}

export default ShelfLife;
